import { useState } from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Header } from "@/components/header"
import { VoteButton } from "@/components/vote-button"
import { MiniWaveform } from "@/components/mini-waveform"
import { PlayButton } from "@/components/play-button"
import { useSamplePlayer } from "@/hooks/use-sample-player"
import { downloads } from "@/services/downloads"
import { displayName } from "@/lib/utils"
import { Download, Pencil } from "lucide-react"
import type Sample from "@/types/generated/Sample"
import type { SharedProps } from "@/types"

type Props = {
  sample: Sample
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function Show({ sample }: Props) {
  const { currentUser } = usePage<SharedProps>().props
  const isOwner = currentUser?.id === sample.user.id
  const [downloadsCount, setDownloadsCount] = useState(sample.downloads_count)
  const [isDownloading, setIsDownloading] = useState(false)
  const {
    isPlaying,
    isLoading,
    progress,
    currentTime,
    duration,
    canPlay,
    handlePlayPause,
    handleSeek,
  } = useSamplePlayer(sample)

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const { download_url, downloads_count } = await downloads.track(sample.id)
      setDownloadsCount(downloads_count)
      window.location.href = download_url
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="min-h-screen">
      <Head title={sample.title} />
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-6">
              <Link href={`/users/${sample.user.id}`}>
                <Avatar className="h-12 w-12 hover:ring-2 hover:ring-primary transition-all">
                  <AvatarImage src={sample.user.avatar_url ?? undefined} />
                  <AvatarFallback>{displayName(sample.user)[0]}</AvatarFallback>
                </Avatar>
              </Link>

              <div className="flex-1">
                <h1 className="text-xl font-bold glitch-subtle" data-text={sample.title}>{sample.title}</h1>
                <p className="text-muted-foreground">
                  by{" "}
                  <Link
                    href={`/users/${sample.user.id}`}
                    className="hover:text-foreground transition-colors"
                  >
                    {displayName(sample.user)}
                  </Link>
                </p>
              </div>
            </div>

            {/* Audio Player */}
            <div className="bg-black/40 rounded-md mb-6 p-4">
              <div className="flex items-center gap-4">
                <PlayButton
                  isPlaying={isPlaying}
                  isLoading={isLoading}
                  disabled={!canPlay}
                  onClick={handlePlayPause}
                  size="lg"
                />

                <div className="flex-1">
                  {sample.waveform ? (
                    <MiniWaveform
                      peaks={sample.waveform}
                      progress={progress}
                      isPlaying={isPlaying}
                      onClick={canPlay ? handleSeek : undefined}
                      className="h-16"
                    />
                  ) : (
                    <div className="h-16 flex items-center justify-center">
                      <span className="text-muted-foreground text-sm font-mono">
                        {sample.analysis_status === "pending" && "Analyzing..."}
                        {sample.analysis_status === "processing" && "Processing..."}
                        {sample.analysis_status === "failed" && "Analysis failed"}
                        {sample.analysis_status === "completed" && "No waveform"}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between mt-2 text-xs text-muted-foreground font-mono">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div>
                <div className="text-xs text-muted-foreground uppercase">BPM</div>
                <div className="font-mono font-medium">{sample.bpm ?? "--"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase">Key</div>
                <div className="font-mono font-medium">{sample.key ?? "--"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase">Duration</div>
                <div className="font-mono font-medium">{formatTime(duration)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase">Size</div>
                <div className="font-mono font-medium">{formatFileSize(sample.file_size)}</div>
              </div>
            </div>

            {/* Description */}
            {sample.description && (
              <p className="text-muted-foreground mb-6">{sample.description}</p>
            )}

            {/* Tags */}
            {sample.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {sample.tags.map((tag) => (
                  <Badge key={tag.id} variant="secondary">
                    {tag.name}
                  </Badge>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-4">
              {currentUser ? (
                <Button className="btn-ghost-shadow" onClick={handleDownload} disabled={isDownloading}>
                  <Download className="h-4 w-4 mr-2" />
                  {isDownloading ? "Downloading..." : "Download"}
                </Button>
              ) : (
                <Button asChild>
                  <a href={`/login?return_to=/samples/${sample.id}`}>
                    <Download className="h-4 w-4 mr-2" />
                    Sign in to download
                  </a>
                </Button>
              )}

              <VoteButton
                sampleId={sample.id}
                voted={sample.voted}
                votesCount={sample.votes_count}
              />

              {isOwner && (
                <Button variant="secondary" asChild>
                  <Link href={`/samples/${sample.id}/edit`}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Link>
                </Button>
              )}

              <span className="text-sm text-muted-foreground font-mono ml-auto">
                {downloadsCount} downloads
              </span>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  )
}
